import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import routes from '../../../Routes.module.css';
import TileColumn from '../../../../Components/Utils/Tile/TileColumn/TileColumn';
import TileRow from '../../../../Components/Utils/Tile/TileRow/TileRow';
import Tile from '../../../../Components/Utils/Tile/Tile';
import styles from './ImproveTemplateCreation.module.css';
import ToggleSlider from '../../../../Components/Utils/ToggleSlider/ToggleSlider';
import DropDownList from '../../../../Components/Utils/DropDownList/DropDownList';
import { IDropDownListItem } from '../../../../Types/DropDownList/IDropDownListItem';
import Button from '../../../../Components/Utils/Button/Button';
import { ImproveTemplateDto, FieldConfigDto } from '../../../../Types/Improve/ImproveTemplateDto';
import { GetTemplate } from '../../../../API/Improve/Improve';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';
import { toast } from 'react-toastify';
import { useWorkflowContext } from '../../../../Hooks/useWorkflowContext';

const ImproveTemplateCreation = () => {
  const { templateId } = useParams<{ templateType: string; templateId: string; templateTitle: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<ImproveTemplateDto | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const context = useWorkflowContext();

  useEffect(() => {
    const fetchTemplate = async () => {
      if (templateId) {
        try {
          const data = await GetTemplate(templateId);
          setTemplate(data);
        } catch (error) {
          console.error('Failed to fetch template:', error);
        }
      }
    };

    fetchTemplate();
  }, [templateId]);

  const updateFieldConfig = (field: keyof ImproveTemplateDto, property: keyof FieldConfigDto, value: any) => {
    if (!template) return;
    
    setTemplate(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: {
          ...(prev[field] as FieldConfigDto),
          [property]: value
        }
      };
    });
  };

  const handleSaveTemplate = async () => {
    if (!template) return;
    
    setIsSaving(true);
    try {
      await axiosInstance.post('Improve/UpdateTemplateByDto', template);
      toast.success('Template saved successfully!');
      navigate(context.isAdminContext? '/admin/manage-templates/active' : '/template-editor/active');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  console.log('Current Template State:', template);

  return (
    <div className={routes.container}>
      <div className={styles.body}>
        <h1>Track Template Creation</h1>
        <p>{template?.title}</p>
        <Tile>
          <TileColumn>
          <TileRow>
          <TileColumn>
            <TileRow>
              <TileColumn>
              <h3>Improvements</h3>
              <p className={styles.improveTemplateCreation__sectionHeader}>Default columns</p>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>
                    {template?.status.name || 'Status'}
                  </p>
                  <ToggleSlider 
                    isOn={template?.status.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('status', 'shown', state)}
                    disableConfirm
                  />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Objective'}</p>
                  <ToggleSlider 
                    isOn={template?.objective.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('objective', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.objective.name || ''} onChange={(e) => updateFieldConfig('objective', 'name', e.target.value)} placeholder="Enter objective vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Assigned to'}</p>
                                    <ToggleSlider 
                    isOn={template?.assignedTo.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('assignedTo', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.assignedTo.name || ''} onChange={(e) => updateFieldConfig('assignedTo', 'name', e.target.value)} placeholder="Enter assigned to vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Expected Impact'}</p>
                                    <ToggleSlider 
                    isOn={template?.expectedImpact.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('expectedImpact', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.expectedImpact.name || ''} onChange={(e) => updateFieldConfig('expectedImpact', 'name', e.target.value)} placeholder="Enter expected impact vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Impact on completion'}</p>
                                    <ToggleSlider 
                    isOn={template?.actualImpact.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('actualImpact', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.actualImpact.name || ''} onChange={(e) => updateFieldConfig('actualImpact', 'name', e.target.value)} placeholder="Enter actual impact vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Target date'}</p>
                  <ToggleSlider 
                    isOn={template?.targetDate.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('targetDate', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.targetDate.name || ''} onChange={(e) => updateFieldConfig('targetDate', 'name', e.target.value)} placeholder="Enter target date vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
                <TileRow>
                  <div className={styles.improveTemplateCreationToggle}>
                    <p className={styles.improveTemplateCreationToggle__label}>{'Completion date'}</p>
                    <ToggleSlider 
                      isOn={template?.completionDate.shown ?? false} 
                      onToggle={(state) => updateFieldConfig('completionDate', 'shown', state)}
                      disableConfirm
                    />
                    <input type="text" value={template?.completionDate.name || ''} onChange={(e) => updateFieldConfig('completionDate', 'name', e.target.value)} placeholder="Enter completion date vocabulary" className={styles.improveTemplateCreationToggle__input} />
                  </div>
                </TileRow>
                <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Tags'}</p>
                  <ToggleSlider 
                    isOn={template?.tags.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('tags', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.tags.name || ''} onChange={(e) => updateFieldConfig('tags', 'name', e.target.value)} placeholder="Enter tags vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Comments'}</p>
                  <ToggleSlider 
                    isOn={template?.comments.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('comments', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.comments.name || ''} onChange={(e) => updateFieldConfig('comments', 'name', e.target.value)} placeholder="Enter comments vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              </TileColumn>
            </TileRow>
          </TileColumn>
          </TileRow>
          <TileRow>
            <TileColumn>
              <TileRow>
              <p className={styles.improveTemplateCreation__sectionHeader}>Optional Columns</p>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Text Column A'}</p>
                  <ToggleSlider 
                    isOn={template?.columnA.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('columnA', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.columnA.name || ''} onChange={(e) => updateFieldConfig('columnA', 'name', e.target.value)} placeholder="Enter text column A vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Text Column B'}</p>
                  <ToggleSlider 
                    isOn={template?.columnB.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('columnB', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.columnB.name || ''} onChange={(e) => updateFieldConfig('columnB', 'name', e.target.value)} placeholder="Enter text column B vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Text Column C'}</p>
                  <ToggleSlider 
                    isOn={template?.columnC.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('columnC', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.columnC.name || ''} onChange={(e) => updateFieldConfig('columnC', 'name', e.target.value)} placeholder="Enter text column C vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Date Column A'}</p>
                  <ToggleSlider 
                    isOn={template?.customDateA.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('customDateA', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.customDateA.name || ''} onChange={(e) => updateFieldConfig('customDateA', 'name', e.target.value)} placeholder="Enter date column A vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Tag Column A'}</p>
                  <ToggleSlider 
                    isOn={template?.customTagA.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('customTagA', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.customTagA.name || ''} onChange={(e) => updateFieldConfig('customTagA', 'name', e.target.value)} placeholder="Enter tag column A vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>

              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'URL Column 1'}</p>
                  <ToggleSlider 
                    isOn={template?.url1.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('url1', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.url1.name || ''} onChange={(e) => updateFieldConfig('url1', 'name', e.target.value)} placeholder="Enter URL column 1 vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
            <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'URL Column 2'}</p>
                  <ToggleSlider 
                    isOn={template?.url2.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('url2', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.url2.name || ''} onChange={(e) => updateFieldConfig('url2', 'name', e.target.value)} placeholder="Enter URL column 2 vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
            <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'URL Column 3'}</p>
                  <ToggleSlider 
                    isOn={template?.url3.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('url3', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.url3.name || ''} onChange={(e) => updateFieldConfig('url3', 'name', e.target.value)} placeholder="Enter URL column 3 vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
            </TileColumn>
          </TileRow>
          </TileColumn>
        </Tile>
        <Tile>
          <TileColumn>
            <TileRow>
              <TileColumn>
              <h3>Tasks</h3>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>
                    {template?.taskStatus.name || 'Status'}
                  </p>
                  <ToggleSlider 
                    isOn={template?.taskStatus.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('taskStatus', 'shown', state)}
                    disableConfirm
                  />
                </div>
              </TileRow>
                <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Heading'}</p>
                  <ToggleSlider 
                    isOn={template?.taskHeading.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('taskHeading', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.taskHeading.name || ''} onChange={(e) => updateFieldConfig('taskHeading', 'name', e.target.value)} placeholder="Enter task heading vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Assigned to'}</p>
                                    <ToggleSlider 
                    isOn={template?.taskAssignedTo.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('taskAssignedTo', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.taskAssignedTo.name || ''} onChange={(e) => updateFieldConfig('taskAssignedTo', 'name', e.target.value)} placeholder="Enter assigned to vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
                <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Target date'}</p>
                                    <ToggleSlider 
                    isOn={template?.taskTargetDate.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('taskTargetDate', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.taskTargetDate.name || ''} onChange={(e) => updateFieldConfig('taskTargetDate', 'name', e.target.value)} placeholder="Enter target date vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
                <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Completion date'}</p>
                                    <ToggleSlider 
                    isOn={template?.taskCompletionDate.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('taskCompletionDate', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.taskCompletionDate.name || ''} onChange={(e) => updateFieldConfig('taskCompletionDate', 'name', e.target.value)} placeholder="Enter completion date vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Text Column D'}</p>
                  <ToggleSlider 
                    isOn={template?.columnD.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('columnD', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.columnD.name || ''} onChange={(e) => updateFieldConfig('columnD', 'name', e.target.value)} placeholder="Enter text column D vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Text Column E'}</p>
                  <ToggleSlider 
                    isOn={template?.columnE.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('columnE', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.columnE.name || ''} onChange={(e) => updateFieldConfig('columnE', 'name', e.target.value)} placeholder="Enter text column E vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Text Column F'}</p>
                  <ToggleSlider 
                    isOn={template?.columnF.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('columnF', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.columnF.name || ''} onChange={(e) => updateFieldConfig('columnF', 'name', e.target.value)} placeholder="Enter text column F vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              <TileRow>
                <div className={styles.improveTemplateCreationToggle}>
                  <p className={styles.improveTemplateCreationToggle__label}>{'Tag Column B'}</p>
                  <ToggleSlider 
                    isOn={template?.customTagB.shown ?? false} 
                    onToggle={(state) => updateFieldConfig('customTagB', 'shown', state)}
                    disableConfirm
                  />
                  <input type="text" value={template?.customTagB.name || ''} onChange={(e) => updateFieldConfig('customTagB', 'name', e.target.value)} placeholder="Enter tag column B vocabulary" className={styles.improveTemplateCreationToggle__input} />
                </div>
              </TileRow>
              </TileColumn>
            </TileRow>
              <Button style={{maxWidth: '200px'}} clickHandler={handleSaveTemplate} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Template'}</Button>
          </TileColumn>
        </Tile>
      </div>
    </div>
  );
};

export default ImproveTemplateCreation;
